import { useMemo, useState } from 'react';
import {
  ActionsDialogue, Bouton, Carte, Champ, classeListeDialogue, Dialogue, Message, Vide, useColonnes,
} from '@journal/ui';

/**
 * Un domaine, ses contrôles, et la classe en lignes.
 *
 * ── Pourquoi les élèves en LIGNES ici, alors que la grille les met en colonnes ─
 * La grille par objectifs affronte des centaines d'objectifs pour vingt-cinq
 * élèves : elle met les élèves en colonnes pour ne pas produire un tableau
 * interminable. Un domaine compte trois ou quatre contrôles : le tableau est
 * large de quatre colonnes et haut de vingt-cinq lignes, ce qui se lit et
 * s'imprime comme une liste de classe.
 *
 * ── La moyenne est un NIVEAU, jamais un nombre ──────────────────────────────
 * Elle vient du serveur, calculée avec le même barème que les statistiques. La
 * recalculer ici donnerait deux réponses pour le même élève selon l'écran.
 */

export const NIVEAUX = [
  { valeur: '', abrege: '—', titre: 'Non évalué', couleur: 'transparent' },
  { valeur: 'bleu', abrege: 'NA', titre: 'Non atteint', couleur: '#3b82f6' },
  { valeur: 'orange', abrege: 'PA', titre: 'Partiellement atteint', couleur: '#f97316' },
  { valeur: 'jaune', abrege: 'A', titre: 'Atteint', couleur: '#eab308' },
  { valeur: 'vert', abrege: 'D', titre: 'Dépassé', couleur: '#22c55e' },
] as const;

export const niveau = (valeur: string | null) =>
  NIVEAUX.find((n) => n.valeur === (valeur ?? '')) ?? NIVEAUX[0];

/**
 * Ordre du CLIC : — → D → A → PA → NA → —
 *
 * ── Pourquoi il ne suit pas l'échelle ───────────────────────────────────────
 * L'échelle se lit du plus faible au plus fort — c'est celle du livret, et la
 * légende comme les touches 0 à 4 la gardent. Mais une classe réussit : le
 * niveau le plus souvent posé est le plus haut, et le clic doit donc l'offrir
 * EN PREMIER. Dans l'autre sens, corriger une grille de vingt-cinq élèves
 * demandait quatre clics par case pour le cas le plus fréquent.
 */
const ORDRE_CLIC = ['', 'vert', 'jaune', 'orange', 'bleu'] as const;

/** Niveau suivant au clic, en boucle. */
export function niveauSuivant(actuel: string): string {
  const position = ORDRE_CLIC.indexOf(actuel as (typeof ORDRE_CLIC)[number]);
  // Valeur inconnue — un import, une version plus ancienne : le clic pose le
  // premier niveau plutôt que d'effacer une note qu'on n'a pas su lire.
  if (position < 0) return ORDRE_CLIC[1]!;
  return ORDRE_CLIC[(position + 1) % ORDRE_CLIC.length]!;
}

export interface Eleve { id: string; lastName: string; firstName: string }
export interface Controle { id: string; number: number; label: string }
export interface Domaine {
  name: string;
  color: string;
  assessments: Controle[];
  objectives: string[];
}
export interface Moyenne {
  domainName: string;
  studentId: string;
  level: string | null;
  count: number;
}

export function TableauControles({
  matiere, domaine, eleves, notes, moyennes, surNote, surAjout, surRenommage, surSuppression,
}: {
  /** Pour l'intitulé imprimé : « Français · Lecture ». */
  matiere?: string;
  domaine: Domaine;
  eleves: Eleve[];
  /** Clé « controleId:eleveId » → niveau. */
  notes: Map<string, string>;
  moyennes: Moyenne[];
  surNote: (controleId: string, eleveId: string, niveau: string) => void;
  surAjout: (libelle: string) => Promise<void>;
  surRenommage: (controleId: string, libelle: string) => Promise<void>;
  surSuppression: (controleId: string) => Promise<void>;
}) {
  const [ajout, setAjout] = useState(false);
  const [renommage, setRenommage] = useState<Controle | null>(null);

  /*
   * Largeurs réglables, mémorisées par DOMAINE.
   *
   * Un intitulé long — « Lire à voix haute un texte travaillé » — a besoin
   * d'une colonne large ; « Dictée » n'en demande aucune. Une largeur commune à
   * tous les domaines obligerait à régler pour le pire cas partout.
   */
  const colonnes = useColonnes(`controles.${domaine.name}`);

  const moyenneDe = useMemo(() => {
    const index = new Map<string, Moyenne>();
    for (const m of moyennes) {
      if (m.domainName === domaine.name) index.set(m.studentId, m);
    }
    return index;
  }, [moyennes, domaine.name]);

  return (
    <Carte
      // Le tableau va au bord : il a ses bordures, la carte lui en ajoutait
      // une seconde à vingt pixels de la première.
      sansRembourrage
      titre={domaine.name}
      action={
        <Bouton variante="secondary" taille="sm" onClick={() => setAjout(true)}>
          Ajouter un contrôle
        </Bouton>
      }
    >
      {domaine.assessments.length === 0 ? (
        <Vide titre="Aucun contrôle dans ce domaine">
          Ajoutez-en un pour commencer à noter la classe.
        </Vide>
      ) : (
        <div data-impression="evaluation" className="impression-paysage overflow-x-auto">
          {/*
            * L'intitulé du domaine, sur la feuille seulement : à l'écran, la
            * carte le porte déjà. Sans lui, les grilles d'une matière
            * s'enchaînaient sur le papier sans dire à quel domaine chacune
            * correspondait.
            */}
          <h2 className="titre-bloc impression-seule hidden">
            {[matiere, domaine.name].filter(Boolean).join(' · ')}
          </h2>
          <table className="w-full border-collapse text-[13px]">
            {colonnes.colgroup([
              'Élève', ...domaine.assessments.map((c) => c.id), 'Moyenne',
            ])}
            <thead>
              <tr>
                <th
                  className="relative sticky left-0 z-10 border-b border-[var(--color-border)] bg-[var(--color-surface)] px-2 py-2 text-left font-medium"
                  style={colonnes.entete('Élève').style}
                >
                  Élève
                  {colonnes.poignee('Élève')}
                </th>
                {domaine.assessments.map((c) => (
                  <th
                    key={c.id}
                    /*
                     * `relative` : la poignée est positionnée en absolu sur le
                     * bord droit de la cellule. Sans repère, elle se plaçait
                     * par rapport au premier ancêtre positionné — c'est-à-dire
                     * n'importe où sur la page.
                     */
                    className="relative border-b border-l border-[var(--color-border)] px-1 py-2 text-center font-medium"
                    style={colonnes.entete(c.id).style}
                  >
                    {/*
                      * L'en-tête est un BOUTON : il ouvre le renommage et la
                      * suppression. `imprimable` le sort de la règle qui masque
                      * les boutons à l'impression — sans elle, les titres de
                      * colonnes disparaissaient de la feuille, défaut que la v1
                      * a dû corriger après coup.
                      */}
                    <button
                      type="button"
                      onClick={() => setRenommage(c)}
                      title="Renommer ou supprimer ce contrôle"
                      className="imprimable w-full rounded px-1 py-0.5 hover:bg-[var(--color-surface-hover)] focus-visible:outline-2 focus-visible:outline-[var(--color-focus)]"
                    >
                      <span className="block font-semibold">Contrôle {c.number}</span>
                      {c.label && (
                        <span className="block text-[11px] font-normal text-[var(--color-fg-muted)]">
                          {c.label}
                        </span>
                      )}
                    </button>
                    {colonnes.poignee(c.id)}
                  </th>
                ))}
                <th
                  className="relative border-b border-l border-[var(--color-border)] px-2 py-2 text-center font-medium"
                  style={colonnes.entete('Moyenne').style}
                >
                  Moyenne
                  {colonnes.poignee('Moyenne')}
                </th>
              </tr>
            </thead>
            <tbody>
              {eleves.map((e) => {
                const m = moyenneDe.get(e.id);
                const infoMoyenne = niveau(m?.level ?? null);
                return (
                  <tr key={e.id}>
                    <td className="sticky left-0 z-10 border-b border-[var(--color-border)] bg-[var(--color-surface)] px-2 py-1.5 whitespace-nowrap">
                      {e.lastName} {e.firstName}
                    </td>
                    {domaine.assessments.map((c) => {
                      const valeur = notes.get(`${c.id}:${e.id}`) ?? '';
                      const info = niveau(valeur);
                      return (
                        <td
                          key={c.id}
                          className="border-b border-l border-[var(--color-border)] p-0 text-center"
                        >
                          <button
                            type="button"
                            onClick={() => surNote(c.id, e.id, niveauSuivant(valeur))}
                            onKeyDown={(ev) => {
                              // Les touches 0 à 4 posent un niveau directement :
                              // plus rapide que quatre clics pour redescendre.
                              if (ev.key >= '0' && ev.key <= '4') {
                                ev.preventDefault();
                                surNote(c.id, e.id, NIVEAUX[Number(ev.key)]!.valeur);
                              }
                            }}
                            aria-label={`${e.firstName} ${e.lastName}, Contrôle ${c.number} · ${info.titre}`}
                            title={`${info.titre} · clic pour changer, touches 0 à 4 pour choisir`}
                            className="imprimable w-full cursor-pointer px-1 py-2 text-center text-[12px] font-semibold transition-colors duration-100 hover:brightness-95 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-[var(--color-focus)] motion-reduce:transition-none"
                            style={{
                              backgroundColor: valeur ? `${info.couleur}2e` : 'transparent',
                              color: valeur ? 'var(--color-fg)' : 'var(--color-fg-muted)',
                            }}
                          >
                            {info.abrege}
                          </button>
                        </td>
                      );
                    })}
                    <td
                      className="border-b border-l border-[var(--color-border)] px-2 py-1.5 text-center text-[12px] font-semibold"
                      style={{
                        backgroundColor: m?.level ? `${infoMoyenne.couleur}1f` : 'transparent',
                        color: m?.level ? 'var(--color-fg)' : 'var(--color-fg-muted)',
                      }}
                    >
                      {/* Le LIBELLÉ complet, pas l'abrégé : c'est la colonne
                          qu'on lit en réunion, et « PA » n'y dit rien. */}
                      {m?.level ? infoMoyenne.titre : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {ajout && (
        <DialogueControle
          titre={`Nouveau contrôle · ${domaine.name}`}
          libelle=""
          suggestions={domaine.objectives}
          surFermeture={() => setAjout(false)}
          surValidation={async (l) => { await surAjout(l); setAjout(false); }}
        />
      )}

      {renommage && (
        <DialogueControle
          titre={`Contrôle ${renommage.number} · ${domaine.name}`}
          libelle={renommage.label}
          suggestions={domaine.objectives}
          surFermeture={() => setRenommage(null)}
          surValidation={async (l) => { await surRenommage(renommage.id, l); setRenommage(null); }}
          surSuppression={async () => { await surSuppression(renommage.id); setRenommage(null); }}
        />
      )}
    </Carte>
  );
}

/**
 * Saisie de l'intitulé, avec les objectifs du domaine en suggestions.
 *
 * ── Un seul champ, pas deux ─────────────────────────────────────────────────
 * La v1 en proposait deux — saisie libre d'un côté, liste d'objectifs de
 * l'autre — puis les a fusionnés. Deux champs pour une seule information
 * obligent à choisir lequel remplir avant même de savoir ce qu'on veut écrire.
 *
 * ── Une liste maison, pas un `<datalist>` ───────────────────────────────────
 * Le `<datalist>` natif ne se met pas en forme, s'affiche différemment dans
 * chaque navigateur, et tronque les intitulés longs — or un objectif du
 * programme fait couramment cent caractères.
 */
function DialogueControle({
  titre, libelle, suggestions, surFermeture, surValidation, surSuppression,
}: {
  titre: string;
  libelle: string;
  suggestions: string[];
  surFermeture: () => void;
  surValidation: (libelle: string) => Promise<void>;
  surSuppression?: () => Promise<void>;
}) {
  const [valeur, setValeur] = useState(libelle);
  const [confirmation, setConfirmation] = useState(false);

  const proposees = useMemo(() => {
    const recherche = valeur.trim().toLowerCase();
    const filtrees = recherche
      ? suggestions.filter((o) => o.toLowerCase().includes(recherche))
      : suggestions;
    // Bornées à dix : une liste de vingt-deux objectifs pousse le bouton
    // « Enregistrer » hors de l'écran, et l'on croit le formulaire cassé.
    return filtrees.slice(0, 10);
  }, [valeur, suggestions]);

  return (
    <Dialogue ouvert titre={titre} largeur="lg" surFermeture={surFermeture}>
      <div className="flex min-h-0 flex-col gap-3">
        <Champ
          libelle="Intitulé"
          aide="Écrivez librement, ou choisissez un objectif du domaine."
          placeholder="Ex : Dictée de mots"
          value={valeur}
          autoFocus
          onChange={(e) => setValeur(e.target.value)}
        />

        {proposees.length > 0 && (
          <div className={`flex max-h-52 flex-col rounded-lg border border-[var(--color-border)] ${classeListeDialogue}`}>
            {proposees.map((o) => (
              <button
                key={o}
                type="button"
                onClick={() => setValeur(o)}
                className="border-b border-[var(--color-border)] px-3 py-2 text-left text-[13px] last:border-b-0 hover:bg-[var(--color-surface-hover)] focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-[var(--color-focus)]"
              >
                {o}
              </button>
            ))}
          </div>
        )}

        {confirmation && (
          <Message ton="attention" titre="Supprimer ce contrôle ?">
            Ses notes seront effacées, et les contrôles suivants renumérotés.
          </Message>
        )}

        <ActionsDialogue>
          {surSuppression && (
            <Bouton
              variante="danger"
              onClick={() => (confirmation ? void surSuppression() : setConfirmation(true))}
            >
              {confirmation ? 'Confirmer la suppression' : 'Supprimer'}
            </Bouton>
          )}
          <Bouton variante="ghost" onClick={surFermeture}>Annuler</Bouton>
          <Bouton variante="primary" onClick={() => void surValidation(valeur.trim())}>
            Enregistrer
          </Bouton>
        </ActionsDialogue>
      </div>
    </Dialogue>
  );
}
